/**
 * Current Stock API Service
 * @see US-12b - Stock Actuel (Consultation)
 * @see Sprint 27 - Stock a Date
 */

import { apiClient } from '@/api/client';
import type {
  CurrentStockListResponse,
  StockKPIs,
  QueryCurrentStockDto,
  QueryStockBreakdownDto,
  StockBreakdownResponse,
} from '@bstock/shared';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get current stock list with pagination and filters
 * Supports optional date param for historical stock
 */
export async function getCurrentStock(
  params?: QueryCurrentStockDto
): Promise<CurrentStockListResponse> {
  const response = await apiClient.get<ApiResponse<CurrentStockListResponse['data']>>(
    '/stock/current',
    { params }
  );
  return { data: response.data.data, pagination: response.data.pagination! };
}

/**
 * Get stock KPIs
 * @param date - Optional date (YYYY-MM-DD) for historical KPIs
 */
export async function getStockKPIs(date?: string): Promise<StockKPIs> {
  const response = await apiClient.get<ApiResponse<StockKPIs>>('/stock/kpis', {
    params: date ? { date } : undefined,
  });
  return response.data.data;
}

/**
 * Get stock breakdown with aggregated movements by type
 * @see Sprint 27 - Stock a Date
 */
export async function getStockBreakdown(
  rawProductId: string,
  params?: QueryStockBreakdownDto
): Promise<StockBreakdownResponse> {
  const response = await apiClient.get<ApiResponse<StockBreakdownResponse>>(
    `/stock/current/${rawProductId}/breakdown`,
    { params }
  );
  return response.data.data;
}
